import { useEffect, useRef, useState, type RefObject } from 'react'
import { ActionType, DomPosition } from 'klinecharts'
import type { Chart, Crosshair } from 'klinecharts'
import {
  ChevronsDownUp,
  ChevronsUpDown,
  Eye,
  EyeOff,
  Maximize,
  Minimize,
  Settings2,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { summarizeInputs } from '@/indicators'
import { defOf, outputColors } from './indicators'
import { getIndicator } from './types'
import type { IndicatorInstance, ToolIcon } from './types'

// Sub-pane chrome for each bottom indicator pane:
//  • the legend (HTML, top-left): name · params · coloured values — matching the
//    main-pane status line. We render it ourselves (the native canvas legend is
//    suppressed at creation) so the parts can be coloured independently.
//  • the hover cluster (top-left, by the legend): hide · settings · remove.
//  • the pane controls (top-right): collapse/expand · maximize/restore.
//  • collapsed state: the whole pane is covered by a solid background panel so
//    it reads as a plain strip with just the legend + (on hover) the CTAs.
// Boxes are recomputed on every render; the parent bumps `nonce` on scroll/zoom
// so the positions stay glued to the panes.

const COLLAPSED_H = 30
const HIDE_DELAY = 80

const fmtVal = (v: number): string => (Math.abs(v) >= 1 ? v.toFixed(2) : v.toFixed(4))

export interface WorkbenchPaneControlsProps {
  chart: Chart
  indicators: IndicatorInstance[]
  containerRef: RefObject<HTMLDivElement | null>
  /** Bumped by the parent on scroll / zoom / layout so boxes re-measure. */
  nonce: number
  onToggleVisible: (id: string) => void
  onOpenSettings: (id: string) => void
  onRemove: (id: string) => void
}

type PaneMode = 'normal' | 'collapsed' | 'fullscreen'

interface PaneBox {
  id: string
  paneId: string
  /** Legend title (the def's short label). */
  name: string
  /** Unique engine identity for chart reads. */
  engineName: string
  /** Legend inputs text ("14", "12, 26, 9"). */
  paramsText: string
  /** Colour per output (figure) index. */
  figColors: string[]
  textColor?: string
  visible: boolean
  top: number
  left: number
  height: number
  width: number
}

interface LegendFig {
  color: string
  value: number
}

function CtaBtn({ icon: Icon, title, onClick }: { icon: ToolIcon; title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
    >
      <Icon size={13} strokeWidth={1.75} />
    </button>
  )
}

export function WorkbenchPaneControls({
  chart,
  indicators,
  containerRef,
  nonce,
  onToggleVisible,
  onOpenSettings,
  onRemove,
}: WorkbenchPaneControlsProps) {
  const [hoverPane, setHoverPane] = useState<string | null>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null) // crosshair bar index
  const [, setDataNonce] = useState(0) // bump to re-read legend values / boxes
  const [paneState, setPaneState] = useState<Record<string, PaneMode>>({})
  const origHeightRef = useRef<Record<string, number>>({}) // px height before collapse/fullscreen
  const hideTimer = useRef<number | undefined>(undefined)

  void nonce // consumed as a re-render trigger; boxes below re-measure each render

  // Pane boxes, recomputed on every render against the live chart layout.
  const boxes: PaneBox[] = indicators
    .filter((i) => i.paneId !== 'candle_pane')
    .flatMap((inst) => {
      const b = chart.getSize(inst.paneId, DomPosition.Main)
      if (!b) return []
      const def = defOf(inst)
      return [
        {
          id: inst.id,
          paneId: inst.paneId,
          name: def?.shortLabel ?? inst.name,
          engineName: inst.engineName,
          paramsText: def ? summarizeInputs(def, inst.settings.inputs) : '',
          figColors: outputColors(def, inst.settings),
          textColor: inst.settings.textColor,
          visible: inst.settings.visible !== false,
          top: b.top,
          left: b.left,
          height: b.height,
          width: b.width,
        },
      ]
    })

  // Mirror for the mousemove listener (kept fresh after every render).
  const boxesRef = useRef<PaneBox[]>(boxes)
  useEffect(() => {
    boxesRef.current = boxes
  })

  useEffect(() => {
    const onData = () => {
      setHoverIndex(null)
      setDataNonce((n) => n + 1)
    }
    const onCross = (c?: Crosshair) => {
      setHoverIndex(c?.kLineData ? (c.dataIndex ?? null) : null)
    }
    chart.subscribeAction(ActionType.OnDataReady, onData)
    chart.subscribeAction(ActionType.OnCrosshairChange, onCross)
    return () => {
      chart.unsubscribeAction(ActionType.OnDataReady, onData)
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCross)
    }
  }, [chart])

  // Reveal the cluster by the cursor's y position (one listener on the wrapper)
  // so moving onto the cluster keeps the pane "hovered" — no enter/leave flicker.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      const y = e.clientY - rect.top
      const hit = boxesRef.current.find((b) => y >= b.top && y <= b.top + b.height)
      window.clearTimeout(hideTimer.current)
      if (hit) setHoverPane(hit.paneId)
      else hideTimer.current = window.setTimeout(() => setHoverPane(null), HIDE_DELAY)
    }
    const onLeave = () => {
      hideTimer.current = window.setTimeout(() => setHoverPane(null), HIDE_DELAY)
    }
    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseleave', onLeave)
    return () => {
      el.removeEventListener('mousemove', onMove)
      el.removeEventListener('mouseleave', onLeave)
      window.clearTimeout(hideTimer.current)
    }
  }, [containerRef])

  // Apply a pane height, then re-assert next frame (setPaneOptions can be
  // re-laid-out by the engine) and reposition the boxes.
  const applyHeight = (paneId: string, height: number, minHeight: number) => {
    chart.setPaneOptions({ id: paneId, height, minHeight })
    requestAnimationFrame(() => {
      chart.setPaneOptions({ id: paneId, height, minHeight })
      setDataNonce((n) => n + 1)
    })
  }
  const rememberHeight = (paneId: string, cur: PaneMode) => {
    if (cur === 'normal') {
      const b = chart.getSize(paneId, DomPosition.Main)
      if (b) origHeightRef.current[paneId] = b.height
    }
  }
  const restore = (paneId: string) => {
    applyHeight(paneId, origHeightRef.current[paneId] ?? 100, 30)
    setPaneState((s) => ({ ...s, [paneId]: 'normal' }))
  }
  const toggleCollapse = (paneId: string) => {
    const cur = paneState[paneId] ?? 'normal'
    if (cur === 'collapsed') {
      restore(paneId)
      return
    }
    rememberHeight(paneId, cur)
    applyHeight(paneId, COLLAPSED_H, COLLAPSED_H)
    setPaneState((s) => ({ ...s, [paneId]: 'collapsed' }))
  }
  const toggleFull = (paneId: string) => {
    const cur = paneState[paneId] ?? 'normal'
    if (cur === 'fullscreen') {
      restore(paneId)
      return
    }
    rememberHeight(paneId, cur)
    const root = chart.getDom('candle_pane', DomPosition.Root)
    const full = Math.max(120, Math.round((root?.clientHeight ?? 600) * 0.66))
    applyHeight(paneId, full, 30)
    setPaneState((s) => ({ ...s, [paneId]: 'fullscreen' }))
  }

  if (boxes.length === 0) return null

  const list = chart.getDataList()
  const idx = hoverIndex != null && list[hoverIndex] ? hoverIndex : list.length - 1
  const legendFigs = (b: PaneBox): LegendFig[] => {
    const ind = getIndicator(chart, b.paneId, b.engineName)
    if (!ind) return []
    const row = (ind.result?.[idx] ?? {}) as Record<string, unknown>
    const cols = b.figColors
    const figs: LegendFig[] = []
    ind.figures.forEach((f, i) => {
      const value = row[f.key]
      if (typeof value === 'number') figs.push({ color: b.textColor ?? cols[i % cols.length], value })
    })
    return figs
  }

  return (
    <>
      {boxes.map((b) => {
        const st = paneState[b.paneId] ?? 'normal'
        const collapsed = st === 'collapsed'
        const showCluster = hoverPane === b.paneId
        const figs = legendFigs(b)
        const params = b.paramsText
        return (
          <div key={b.paneId}>
            {/* collapsed: solid panel covering the whole pane (plot/grid/axis hidden) */}
            {collapsed && (
              <div
                className="absolute left-0 right-0 z-[9] bg-background"
                style={{ top: b.top, height: b.height }}
              />
            )}
            {/* legend (top-left): name · params · values, then the indicator
                controls — hide · settings · remove — revealed on hover or by a
                click/tap (touch has no hover; dismiss by tapping elsewhere).
                Wraps within the pane so long params/values never run under the
                pane-controls pill. Muted when hidden. */}
            <div
              className={cn(
                'absolute z-10 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-caption tabular-nums pointer-events-auto cursor-pointer select-none rounded-md px-1.5 -mx-1.5 py-0.5 -my-0.5 transition-colors',
                showCluster && 'bg-popover/80 backdrop-blur-sm',
              )}
              style={{ top: b.top + 6, left: b.left + 8, maxWidth: Math.max(120, b.width - 110) }}
              onClick={() => setHoverPane(b.paneId)}
              onPointerUp={(e) => e.stopPropagation()}
            >
              <span className={b.visible ? 'text-foreground' : 'text-muted-foreground/50'}>
                {b.name}
              </span>
              {params && (
                <span className={b.visible ? 'text-muted-foreground/60' : 'text-muted-foreground/40'}>
                  ({params})
                </span>
              )}
              {b.visible &&
                figs.map((f, i) => (
                  <span key={i} style={{ color: f.color }}>
                    {fmtVal(f.value)}
                  </span>
                ))}
              <span className="pointer-events-auto inline-flex items-center gap-0.5 ml-0.5">
                <span
                  className={cn(
                    'transition-opacity',
                    showCluster || !b.visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <CtaBtn
                    icon={b.visible ? Eye : EyeOff}
                    title={b.visible ? 'Hide' : 'Show'}
                    onClick={() => onToggleVisible(b.id)}
                  />
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5 transition-opacity',
                    showCluster ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <CtaBtn icon={Settings2} title="Settings" onClick={() => onOpenSettings(b.id)} />
                  <CtaBtn icon={Trash2} title="Remove indicator" onClick={() => onRemove(b.id)} />
                </span>
              </span>
            </div>
            {/* pane controls: collapse · maximize. Flush to the chart's right edge
                (overlaying the value axis is fine). */}
            <div className="absolute z-10 pointer-events-none" style={{ top: b.top + 4, right: 6 }}>
              <div
                className={cn(
                  'flex items-center gap-0.5 rounded-md border border-border bg-popover/95 shadow-popover px-0.5 py-0.5 transition-opacity',
                  showCluster ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
                )}
              >
                <CtaBtn
                  icon={collapsed ? ChevronsUpDown : ChevronsDownUp}
                  title={collapsed ? 'Expand pane' : 'Collapse pane'}
                  onClick={() => toggleCollapse(b.paneId)}
                />
                <CtaBtn
                  icon={st === 'fullscreen' ? Minimize : Maximize}
                  title={st === 'fullscreen' ? 'Restore pane' : 'Maximize pane'}
                  onClick={() => toggleFull(b.paneId)}
                />
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
